<template>
  <gc-main-layout
    :title="title"
    :isXSmall="isXSmall"
    :isLarge="isLarge"
    :tabs="tabs"
    :selectedIndex="selectedIndex"
    :backButtonEnable="true"
    :backButtonFunction="backButtonFunction"
    :baseView="baseView"
    :formsConfig="formsConfig"
    :key="pageKey"
  >
    <template #headerContent>
      <GcHeaderContentDynamic
        title="Supplier"
        :createModeIsEditMode="true"
        :baseView="baseView"
        :primaryLabel="model.SupplierName"
        :secondaryLabel="model.SupplierCode"
        recordType="Supplier"
        v-if="model.SupplierCode"
      />
    </template>
    <div class="css-v2 split-view">
      <gc-split-view
        :showLeftContainer="showLeftContainer"
        :showRightContainer="showRightContainer"
      >
        <template #leftContent>
          <div class="css-v2 split-view-list">
            <dx-data-grid
              :data-source="dataSourceConfig"
              ref="estimateGrid"
              :columns="columns"
              :height="gridHeight - 20"
              @row-click="onRowClicked"
              @toolbar-preparing="onToolbarPreparing($event)"
              @content-ready="addGridNoResults($event)"
              v-if="model.SupplierCode"
              :selection="{ mode: 'single' }"
              :row-alternation-enabled="true"
              no-data-text="No Grower Intake Estimates recorded yet for this Grower. Use a Season with no existing estimate to start one."
            >
              <DxPaging :page-size="50" :enabled="false" />
              <DxPager
                :showInfo="true"
                :showPageSizeSelector="false"
                :infoText="'({2} items)'"
                :visible="false"
              />

              <DxScrolling
                :mode="'virtual'"
                :row-rendering-mode="'virtual'"
                :preloadEnabled="true"
              />

              <DxSearchPanel :visible="true" :width="200" />

              <template #huller-cell-template="{ data }">
                <span>{{ hullerDisplayText(data.data.HullerCode) }}</span>
              </template>
            </dx-data-grid>
          </div>
        </template>

        <template #rightContent>
          <DxToolbar class="split-view-toolbar">
            <DxItem #default location="before" locate-in-menu="never">
              <div class="toolbar-label" v-if="!pickingSeason">
                <span class="text-primary"
                  >Season {{ selectedSeason.SeasonYear }}</span
                >
              </div>
              <div class="toolbar-label" v-else>
                <DxSelectBox
                  :data-source="seasonPickerOptions"
                  value-expr="Season"
                  :display-expr="(item) => (item ? `${item.Season}` : '')"
                  search-expr="Season"
                  placeholder="Select a Season with no estimate yet"
                  :search-enabled="true"
                  width="260"
                  @value-changed="onSeasonPicked"
                />
              </div>
            </DxItem>
            <DxItem
              :options="toolbar.audit"
              location="after"
              locate-in-menu="auto"
              widget="dxButton"
              :visible="!editMode && !pickingSeason"
            />
            <DxItem
              :options="toolbar.edit"
              location="after"
              locate-in-menu="auto"
              widget="dxButton"
              :visible="!editMode && !pickingSeason && canUpdate"
            />
            <DxItem
              :options="toolbar.save"
              location="after"
              locate-in-menu="auto"
              widget="dxButton"
              :visible="editMode && !pickingSeason && canSave"
            />
            <DxItem
              :options="toolbar.close"
              location="after"
              widget="dxButton"
            />
          </DxToolbar>

          <div class="view-card responsive-paddings" v-if="!pickingSeason">
            <form>
              <DxForm
                :form-data="hullerFormModel"
                :read-only="!editMode"
                :show-colon-after-label="false"
                :show-required-mark="false"
                :show-validation-summary="false"
                :col-count="1"
                label-location="left"
                :items="hullerFormItems"
                validation-group="growerIntakeEstimateForm"
              ></DxForm>
            </form>
          </div>

          <div
            class="view-card responsive-paddings estimate-grid-card"
            v-if="!pickingSeason"
          >
            <div class="estimate-grid-section-label">
              <span>Variety / Estimate</span>
              <!-- <button
                type="button"
                class="add-variety-link"
                :disabled="!editMode"
                @click="addVarietyRow"
              >
                <i class="dx-icon dx-icon-plus"></i>
                ADD VARIETY
              </button> -->
            </div>
            <DxDataGrid
              :data-source="workingRows"
              key-expr="RowKey"
              ref="estimateDetailGrid"
              :show-borders="false"
              :repaint-changes-only="true"
              :column-auto-width="true"
              :no-data-text="
                editMode
                  ? 'No Variety/Estimate lines yet. Use the Add row action above.'
                  : 'No Variety/Estimate lines recorded for this Grower + Season.'
              "
              :columns="estimateColumns"
              @init-new-row="onInitNewRow"
              :column-chooser="{ enabled: false }"
              @toolbar-preparing="onEstimateGridToolbarPreparing"
              @option-changed="onEstimateGridOptionChanged"
              @editor-preparing="onEstimateEditorPreparing"
            >
              <DxPaging :enabled="false" />
              <DxPager :visible="false" />
              <DxEditing
                mode="batch"
                :allow-updating="editMode"
                :allow-adding="editMode"
                :allow-deleting="editMode"
                :use-icons="true"
                :confirm-delete="false"
                :select-text-on-edit-start="true"
                new-row-position="last"
              />
            </DxDataGrid>

            <div class="panel-footer">
              <span>Row Count: {{ rowCount }}</span>
              <span
                >Total:
                <span class="total-value">{{
                  totalEstimateDisplay
                }}</span></span
              >
            </div>
          </div>
        </template>
      </gc-split-view>
    </div>
  </gc-main-layout>
</template>

<script>
/*
Description
    Grower Intake Estimates tab (Almondco - 2116 - Grower Intake Estimates
    Master, technical-design.md §6). Own tab, own route, sibling to the Wave 1
    Grower tab - modeled directly on supplier-contact.vue (the real "List +
    split-view living inside a Supplier Details tab" precedent), not the
    SplitViewTemplate/SplitViewGridTemplate generic Settings templates.

    #76646 (Maintain & Create) replaces Story 1's read-only stub right panel
    with the real Review/Edit detail panel: Huller/Cracker field (dxSelectBox,
    required, active H1/H2 Suppliers only, BR-04) and the Variety/Estimate
    detail grid (dx-data-grid directly, batch edit mode, native Add/Delete row
    actions via DxEditing - AD-04's direct inline delete icon, no duplicate
    Variety per grid - BR-06(c)). Save performs the full delete/update/insert
    reconciliation (BR-05) via POST api/Save_AlmondcoGrowerIntakeEstimate,
    already built in User Story 76512 (API & Data Model) - this task is UI-only.

    Empty-grid Save is split by record state (BR-08/FLAG-IE-04): an existing
    List row's empty submission is confirmed then proceeds to a full delete;
    a brand-new Season (via + ADD NEW, #76651) is blocked outright instead,
    since there is nothing yet to confirm deleting.

    Audit info shown via the toolbar's Audit button is the first loaded detail
    row's CreatedBy/DateCreated/ChangedBy/DateChanged - matching the sibling
    grower-pool-splits.vue's own established precedent (`toolbarRecord =
    rows[0]`) for a header-level audit proxy when the underlying rows are a
    per-line, not per-header, table.

    Row selection is local component state only (selectedSeason), not a route
    parameter - confirmed with the developer during Build (#76611): unlike
    supplier-contact.vue, the List's rows (AlmondcoGrowerIntakeEstimateSummary)
    have no RecordId to reuse for a dual-purpose :id the way that screen does.
    This tab's own route stays fixed to the Supplier's code
    (/grower-intake-estimate/:id) for its entire lifetime. The Save permission
    gate's create-vs-update ternary (§6) is therefore keyed off the local
    `creating` flag instead of baseView.mode.

History
    02Sep2026 (SS)
        - #76611: UI - Grower Intake Estimates List (grower-intake-estimate.vue)
    02Sep2026 (SS)
        - #76646: UI - Grower Intake Estimate detail panel (Review/Edit,
          Huller/Cracker field, Variety/Estimate grid)
    06Sep2026 (SD)
        - #77391 fix: Variety Count column was alignment: "center", not
          "right" - contradicted the value/amount-column right-alignment
          standard (resources/test-case-format-template.md) and the Total
          Estimate column right next to it. Full trace in investigation.md
          under this project folder (Bug #77391) - also flags the same-shape
          defect on grower-pool-splits.vue's own "Grower Count" column,
          deliberately left untouched (different screen, not part of this
          fix).
    07Sep2026 (SD)
        - #77392 fix: Variety/Estimate grid never auto-added a blank row -
          the "+ ADD VARIETY" button (addVarietyRow) was the only path to a
          new row, contradicting the "transaction-detail-grid convention"
          this screen's own technical-design.md §6 cites (already shipped
          elsewhere in GCORPcx - Customer Receipt's Unallocated Receipt
          grid). Full trace in investigation.md under this project folder
          (Bug #77392). Added ensureTrailingBlankVarietyRow - the batch-mode
          equivalent of scheme-prices.vue's own cell-mode
          ensureTrailingBlankRow, keyed off mergedEstimateRows/
          pendingEstimateChanges rather than a workingRows watch, since batch
          mode doesn't mutate workingRows until Save - wired into
          onEstimateGridOptionChanged (every in-progress edit),
          enterEditMode (Review->Edit transition), and onSeasonPicked (Add
          New's own grid-reveal moment, the earliest point the grid ref
          exists). rowCount now filters out the always-present blank row the
          same way scheme-prices.vue's realPriceRowCount does (totalEstimate
          needed no equivalent filter - IntakeEstimate defaults to 0).
          Also added discardBlankVarietyRows, called from saveEstimate
          before saveEditData() - without it, VarietyCode's own native
          "required" validationRule would fail on the still-blank trailing
          row every time Save is clicked, silently aborting Save via the
          existing hasEditData() check (a regression the auto-add fix would
          otherwise have introduced, confirmed by tracing saveEstimate
          directly rather than assumed - see investigation.md's own
          Recommended fix #3). The manual "+ ADD VARIETY" button/
          addVarietyRow is unchanged - stays as a convenience alongside
          auto-add, per the Bug's own Expected Outcome.
        - Bulk-validation convention (developer direction, no numbered ADO
          task, same convention as scheme-prices.vue): removed the
          DevExtreme inline validationRules from both hullerFormItems'
          Huller/Cracker field (required) and estimateColumns' VarietyCode
          column (required + the custom validateNoDuplicateVariety
          callback, now deleted) - all Save-time validation for this screen
          now goes through a single new validateBeforeSave method instead,
          called from saveEstimate right after discardBlankVarietyRows/
          saveEditData commit the batch into workingRows. Removing the
          native "required" rule on VarietyCode also means grid.saveEditData()
          no longer rejects the always-present trailing blank row on its
          own - discardBlankVarietyRows (added for #77392) is now the ONLY
          thing preventing that row from being committed and saved as a
          real (bad) line, not merely a workaround for a rule that would
          otherwise catch it. The duplicate-Variety check that used to
          surface immediately on typing (via the grid's own live inline
          callback) now surfaces only at Save time instead, same trade-off
          scheme-prices.vue already made for its own duplicate-combination
          check. Note (flagged, not acted on): removing hullerFormItems' own
          validationRules also removes that field's live red-border/
          required-indicator styling in create mode (ui-ux-guidelines.md's
          own convention) - that styling was driven entirely by this same
          DxForm validationRules array, with no separate cosmetic-only flag
          to preserve it; not restored here since the request was
          specifically to remove inline validation, not preserve the visual
          affordance separately.
    06Sep2026 (SD)
        - #77393 fix: the row delete (trash) icon never showed a
          confirmation dialog - clicking it silently greyed out the row and
          updated the footer Total/Row Count, and clicking again restored
          it, with no confirm step at any point. Confirmed via the installed
          DevExtreme source (not inferred) that this isn't a bug in this
          screen's own code: ui.grid_core.editing_cell_based.js's
          _checkAndDeleteRow override calls _deleteRowCore directly whenever
          mode="batch" is in effect, completely bypassing the base
          implementation's confirmDelete/confirm(...) branch - DxEditing's
          native confirm-delete option has no effect at all in batch mode,
          regardless of configuration. Full trace in investigation.md under
          this project folder (Bug #77393). Fix: estimateColumns' delete
          button is now a custom button (name/icon/onClick) instead of the
          `buttons: ["delete"]` shorthand, with its own onDeleteVarietyRowClick
          handler that awaits this.$confirm(...) before calling
          e.component.deleteRow(...) - the same convention every other
          confirm-before-delete grid in this codebase already uses (e.g.
          gc-popup-shifts.vue). DxEditing's own confirm-delete is now false
          (dead config for batch mode, removed rather than left misleading).
          The pending-removal grey/restore toggle itself is unchanged -
          that's DevExtreme's own standard batch-mode behaviour once a
          removal is queued, and it was already working correctly; the only
          gap was the missing confirmation gate ahead of it.
        - Developer request (no numbered ADO task): once a Variety is
          selected in the grid, focus now moves straight to that row's
          Intake Estimate cell instead of DevExtreme's own default (staying
          put/closing the editor with no further navigation). Added
          onEstimateEditorPreparing (VarietyCode's lookup editor gains an
          onSelectionChanged, the same hook point scheme-prices.vue's own
          ItemCode branch uses) and focusIntakeEstimateCell, which mirrors
          the getCellElement+focus+deferred-editCell sequence already
          established elsewhere in this codebase (journal-v2.vue/
          distribution-manifest.vue's own focusOnEditableCell) for reliably
          moving edit-focus to a specific cell.
    07Sep2026 (SD)
        - Bug fix (no numbered ADO task): the above onSelectionChanged also
          fired DevExtreme's own initial resolve when the editor opened on a
          row that already had a VarietyCode - not just a genuine new pick -
          moving focus to Intake Estimate before the user could interact
          with the dropdown at all, so clicking an already-populated Variety
          cell to edit it appeared to go straight to an uneditable state.
          onEstimateEditorPreparing now captures the row's VarietyCode at
          the moment the editor opens and only moves focus when the newly
          selected item's Code actually differs from it - see that method's
          own comment for the full trace (dxSelectBox's SelectionChangedInfo
          carries only selectedItem, no event/previousItem, confirmed
          against the installed DevExtreme type definitions, so the guard
          has to compare against the row's own prior value directly).
        - Developer request (no numbered ADO task): validateBeforeSave's
          duplicate-Variety message now names the specific Variety
          (`Variety "NP - NONPAREIL" has already been entered on another
          row...`) instead of a generic "This Variety has already been
          entered." Added varietyDisplayText (mirrors hullerDisplayText) to
          resolve a Code against varietyOptions. Duplicates are still
          reported one at a time, for free - returning on the first match
          found already means fixing that row and clicking Save again
          re-runs the same check from scratch and surfaces the next
          duplicate, if any, with no separate "already reported" state
          needed.

(c) Copyright Graycorp Pty Ltd (ABN 12 082 807 761). All Rights Reserved.
*/
import BaseViewMixin from "../../v1/_mixins/base.view";
import { FeatureStorage } from "../../_services/storage";
import { SupplierService } from "../../_services/api/suppliers/supplier.service";
import { AlmondcoGrowerIntakeEstimateService } from "../../_services/api/suppliers/almondco-grower-intake-estimate.service";
import ColumnWidth from "../../_helpers/data-grid/ColumnWidth";
import { formatNumber } from "devextreme/localization";
import { DxDataGrid, DxEditing } from "devextreme-vue/data-grid";
import {
  SupplierDetailTabs,
  SupplierDetailTabsVisibility,
  IsGrowerTabVisible,
  GetIndexByTabName,
} from "../../_models/tabdata/suppliers/supplier-detail-tabs";

export default {
  components: { DxDataGrid, DxEditing },
  mixins: [BaseViewMixin],
  created() {
    // Looked up by name, not hardcoded, since the tab strip's order lives in
    // the shared supplier-detail-tabs.js module and can be reordered there
    // independently of this file.
    this.selectedIndex = GetIndexByTabName("Grower Intake Estimates");
    this.loadHullerOptions();
    this.loadVarietyOptions();
  },
  data() {
    return {
      ColumnWidth,
      hullerOptions: [],
      varietyOptions: [],
      selectedSeason: null,
      editMode: false,
      creating: false,
      pickingSeason: false,
      seasonPickerOptions: [],
      workingHuller: "",
      workingRows: [],
      nextRowKey: 1,
      auditInfo: {},
      // Mirrors the estimate grid's own editing.changes option, refreshed
      // only inside onEstimateGridOptionChanged - see mergedEstimateRows.
      pendingEstimateChanges: [],
      toolbar: {
        close: {
          icon: "close",
          onClick: this.closePanel,
        },
        audit: {
          icon: "clock",
          onClick: () => {
            this.displayAuditPopup(
              this.auditInfo.CreatedBy,
              this.auditInfo.DateCreated,
              this.auditInfo.ChangedBy,
              this.auditInfo.DateChanged,
            );
          },
        },
        edit: {
          icon: "edit",
          onClick: this.enterEditMode,
        },
        save: {
          icon: "floppy",
          onClick: this.saveEstimate,
        },
      },
      model: {
        SupplierCode: "",
        SupplierName: "",
      },
    };
  },
  computed: {
    columns() {
      return [
        {
          dataField: "SeasonYear",
          caption: "Season",
          dataType: "number",
          format: "#",
          customizeText: (cellInfo) => `${cellInfo.value}`,
          width: ColumnWidth.SeasonYear.width + 40,
          minWidth: ColumnWidth.SeasonYear.minWidth + 40,
          allowHiding: false,
          sortOrder: "desc",
          alignment: "left",
        },
        {
          dataField: "HullerCode",
          caption: "Huller",
          cellTemplate: "huller-cell-template",
          allowHiding: false,
        },
        {
          dataField: "VarietyCount",
          caption: "Variety Count",
          dataType: "number",
          // #77391 fix (06Sep2026): was "center" - a numeric value column,
          // not an identifier, so it follows the same right-alignment
          // convention as Total Estimate right next to it
          // (resources/test-case-format-template.md's "columns representing
          // a value or amount are right-aligned"; matches 4 sibling
          // ColumnWidth.ItemCount count columns elsewhere in the codebase -
          // inshell-product.vue/scheme-prices.vue/grower-syndicate-splits.vue/
          // label-printing-list.vue - all already "right"). No deliberate
          // reasoning found for "center" (git history: set once in the
          // original #76611 commit, never revisited).
          alignment: "right",
          width: ColumnWidth.ItemCount.width,
          minWidth: ColumnWidth.ItemCount.minWidth,
        },
        {
          dataField: "TotalEstimate",
          caption: "Total Estimate",
          dataType: "number",
          format: "###,##0.00",
          alignment: "right",
          width: ColumnWidth.Amount.width,
          minWidth: ColumnWidth.Amount.minWidth,
        },
      ];
    },
    // §6: Huller/Cracker field - required on Save (BR-04), editable only in
    // Edit mode, coded-lookup display convention (Code - Name).
    hullerFormModel() {
      return { HullerCode: this.workingHuller };
    },
    hullerFormItems() {
      const vm = this;
      return [
        {
          itemType: "group",
          caption: "",
          items: [
            {
              dataField: "HullerCode",
              label: { text: "Huller" },
              editorType: "dxSelectBox",
              editorOptions: {
                dataSource: this.hullerOptions,
                valueExpr: "Code",
                displayExpr: (item) =>
                  item ? `${item.Code} - ${item.Name}` : "",
                searchEnabled: true,
                searchExpr: ["Code", "Name"],
                showClearButton: true,
                onValueChanged: (e) => {
                  vm.workingHuller = e.value;
                },
              },
              // Bulk-validation convention (developer direction, no numbered
              // ADO task, same convention as scheme-prices.vue): no more
              // DevExtreme inline validationRules here - Huller/Cracker's
              // requiredness is checked once, in validateBeforeSave, at Save
              // time instead. See validateBeforeSave's own comment.
            },
          ],
        },
      ];
    },
    // §6/AD-04: Variety (searchable lookup against active PRODCAT1, no
    // duplicate within the grid - BR-06(a)/(c)) and Intake Estimate
    // (decimal(14,2), no min/max/zero validation - FLAG-IE-03). Row delete is
    // still DxEditing's own native direct inline icon (one row action only,
    // AD-04), gated to Edit mode.
    //
    // #77393 fix (06Sep2026): the buttons column's delete action is now a
    // custom button with its own onClick, not the `buttons: ["delete"]`
    // shorthand. Confirmed via the installed DevExtreme source
    // (ui.grid_core.editing_cell_based.js's `_checkAndDeleteRow` override)
    // that DxEditing's native confirmDelete/confirmDeleteMessage options are
    // silently bypassed whenever mode="batch" - _checkAndDeleteRow calls
    // _deleteRowCore directly for batch mode, never reaching the base
    // implementation's confirm(...) branch. That's a framework limitation,
    // not something fixable via editing config (see investigation.md for
    // Bug #77393's full source trace) - DxEditing's own confirm-delete is
    // now false, and this custom button's onClick does the confirm+delete
    // sequence itself instead, matching the same await this.$confirm(...)
    // convention every other confirm-before-delete grid in this codebase
    // already uses (e.g. stock-transfer.vue, supplier-invoice.vue).
    //
    // Bulk-validation convention (developer direction, no numbered ADO task,
    // same convention as scheme-prices.vue): no more DevExtreme inline
    // validationRules on VarietyCode - requiredness and BR-06(c)'s no-
    // duplicate-Variety rule are both checked once, in validateBeforeSave, at
    // Save time instead (previously native per-cell rules, run again inside
    // grid.saveEditData() - the exact mechanism #77392's discardBlankVarietyRows
    // had to work around for the always-present trailing blank row).
    // validateNoDuplicateVariety (the old validationCallback) is gone -
    // superseded by validateBeforeSave's own duplicate check.
    estimateColumns() {
      return [
        {
          dataField: "VarietyCode",
          caption: "Variety",
          minWidth: 220,
          allowEditing: this.editMode,
          lookup: {
            dataSource: this.varietyOptions,
            valueExpr: "Code",
            // #E2E fix: ProductCategory1's real display column is
            // Description, not Name.
            displayExpr: (item) =>
              item ? `${item.Code} - ${item.Description}` : "",
          },
        },
        {
          dataField: "IntakeEstimate",
          caption: "Intake Estimate",
          dataType: "number",
          format: "###,##0.00",
          alignment: "right",
          allowEditing: this.editMode,
          // valueChangeEvent "keyup" so the footer Total (see totalEstimate)
          // updates as the value is typed, not just on blur.
          editorOptions: { format: "###,##0.00", valueChangeEvent: "keyup" },
          width: ColumnWidth.Amount.width,
        },
        {
          caption: "",
          dataField: "",
          type: "buttons",
          alignment: "right",
          maxWidth: 50,
          width: 50,
          buttons: [
            {
              name: "delete",
              hint: "Remove row",
              icon: "trash",
              onClick: this.onDeleteVarietyRowClick,
            },
          ],
          visible: this.editMode,
        },
      ];
    },
    // BR-07: batch edit mode doesn't mutate workingRows until Save, so a
    // plain workingRows-based sum went stale the moment a row was added,
    // edited, or deleted without saving first.
    //
    // First attempt read live cellValue straight from the grid instance
    // inside this computed (the same technique the old
    // validateNoDuplicateVariety inline-validation callback used, since
    // superseded by validateBeforeSave's own bulk check) - that turned out
    // timing-fragile: Vue re-evaluates
    // computeds synchronously on any unrelated render (e.g. toggling
    // editMode itself), which can land before DevExtreme's own internal
    // grid state has settled, and getVisibleRows() came back momentarily
    // empty (Row Count showing 0 with an existing row still on screen).
    // Reconciling against pendingEstimateChanges instead - a plain
    // reactive snapshot refreshed only inside onEstimateGridOptionChanged,
    // the one point guaranteed to run after DevExtreme's editing.changes
    // has actually updated - avoids that render-order dependency entirely.
    totalEstimate() {
      return this.mergedEstimateRows.reduce(
        (sum, r) => sum + (parseFloat(r.IntakeEstimate) || 0),
        0,
      );
    },
    // Same staleness reasoning as totalEstimate - kept in sync so Row Count
    // never shows a stale count while Total already reflects an in-progress
    // add/edit/delete.
    //
    // #77392 fix (06Sep2026): filters out the always-present, still-unfilled
    // trailing blank row (VarietyCode null - see ensureTrailingBlankVarietyRow's
    // own comment) the same way scheme-prices.vue's own realPriceRowCount
    // excludes its equivalent PriceType-null sentinel row - otherwise Row
    // Count would read one higher than the actual number of entered lines
    // the whole time a user is editing. totalEstimate needs no equivalent
    // filter - IntakeEstimate defaults to 0 on a blank row (onInitNewRow), so
    // it never contributes to the sum regardless.
    rowCount() {
      return this.mergedEstimateRows.filter((r) => r.VarietyCode).length;
    },
    // workingRows reconciled against any in-progress, unsaved batch edits.
    // pendingEstimateChanges mirrors the grid's own editing.changes option
    // (insert/update/remove entries), so this reflects what the grid is
    // actually showing right now, not just what was last saved.
    mergedEstimateRows() {
      if (this.pendingEstimateChanges.length === 0) return this.workingRows;

      const removedKeys = new Set(
        this.pendingEstimateChanges
          .filter((c) => c.type === "remove")
          .map((c) => c.key),
      );
      const updateByKey = new Map(
        this.pendingEstimateChanges
          .filter((c) => c.type === "update")
          .map((c) => [c.key, c.data]),
      );
      const insertedRows = this.pendingEstimateChanges
        .filter((c) => c.type === "insert")
        .map((c) => c.data);

      return this.workingRows
        .filter((r) => !removedKeys.has(r.RowKey))
        .map((r) => {
          const patch = updateByKey.get(r.RowKey);
          return patch ? { ...r, ...patch } : r;
        })
        .concat(insertedRows);
    },
    // Matches the grid columns' own "###,##0.00" format (thousands
    // separator + 2dp), rather than toFixed(2)'s unseparated digit string.
    totalEstimateDisplay() {
      return formatNumber(this.totalEstimate, "###,##0.00");
    },
    // #E2E fix: FeatureStorage.checkPermission only recognises modes
    // "create"/"edit"/"review" (feature.storage.js) - "update" matched
    // neither branch and left this permanently false for every user,
    // hiding the Edit button outright. Confirmed live: the edit/floppy
    // toolbar buttons stayed dx-state-invisible for gc-admin.
    canUpdate() {
      return FeatureStorage.checkPermission(
        "AlmondcoGrowerIntakeEstimates",
        "edit",
      );
    },
    // §6: Save permission gate uses 'create' only when this is a brand-new
    // Season (via + ADD NEW, #76651) - 'edit' otherwise. Local `creating`
    // flag stands in for baseView.mode == "create" here, since this screen's
    // routing does not use baseView.mode for this distinction (see #76611).
    canSave() {
      return FeatureStorage.checkPermission(
        "AlmondcoGrowerIntakeEstimates",
        this.creating ? "create" : "edit",
      );
    },
    canAddNew() {
      return FeatureStorage.checkPermission(
        "AlmondcoGrowerIntakeEstimates",
        "create",
      );
    },
    dataSourceConfig() {
      return AlmondcoGrowerIntakeEstimateService.dataSourceConfig(
        "api/AlmondcoGrowerIntakeEstimateSummary",
        {
          filter: [["GrowerCode", "=", this.model.SupplierCode]],
        },
      );
    },
    tabs() {
      // Almondco - 2116 - Grower Master: repointed from an inline array to
      // the shared tab-data module (also fixes the pre-existing Transactions
      // link, which pointed at baseView.id instead of the Supplier code the
      // other tabs used). Gating on loadingCompleted is required for the
      // Grower visibility flag set in handleLocalRouteChange() to ever
      // reach the rendered tab strip - see supplier-detail.vue's tabs().
      if (this.loadingCompleted)
        return SupplierDetailTabs(this.model.SupplierCode);
      else return [];
    },
    showLeftContainer() {
      return !(this.isXSmall && this.selectedSeason);
    },
    showRightContainer() {
      return !!this.selectedSeason;
    },
  },
  watch: {
    $route: {
      immediate: true,
      handler: "handleLocalRouteChange",
    },
  },
  methods: {
    handleLocalRouteChange() {
      if (!this.model.SupplierCode) {
        SupplierService.getByCode(this.baseView.id).then(async (response) => {
          if (response.data.value.length > 0) {
            this.model.SupplierCode = response.data.value[0].Code;
            this.model.SupplierName = response.data.value[0].Name;
            // Must be awaited before loadingCompleted flips to true below -
            // tabs() only re-evaluates when loadingCompleted actually
            // changes, so setting it before this resolves would lock in
            // the stale/default visibility value permanently.
            await IsGrowerTabVisible(
              response.data.value[0].SupplierTypeCode,
            ).then((visible) => {
              // Bug fix (ADO #78320, 2026-09-16): was previously reported
              // under text: "Grower", which silently overwrote the Grower
              // tab's own visibility flag instead of gating this tab.
              SupplierDetailTabsVisibility({
                text: "Grower Intake Estimates",
                value: visible,
              });
            });
          }
          this.loadingCompleted = true;
        });
      } else {
        this.loadingCompleted = true;
      }
    },
    async loadHullerOptions() {
      try {
        const res =
          await AlmondcoGrowerIntakeEstimateService.getHullerOptions();
        this.hullerOptions = (res.data && res.data.value) || [];
      } catch (err) {
        this.hullerOptions = [];
      }
    },
    async loadVarietyOptions() {
      try {
        const res =
          await AlmondcoGrowerIntakeEstimateService.getVarietyOptions();
        this.varietyOptions = (res.data && res.data.value) || [];
      } catch (err) {
        this.varietyOptions = [];
      }
    },
    hullerDisplayText(code) {
      if (!code) return "";
      const huller = this.hullerOptions.find((h) => h.Code === code);
      return huller ? `${huller.Code} - ${huller.Name}` : code;
    },
    // Mirrors hullerDisplayText above - used by validateBeforeSave's own
    // duplicate-Variety message (developer request, no numbered ADO task)
    // so the message names the specific Variety, not just "This Variety".
    // Falls back to the bare code if varietyOptions hasn't resolved a match
    // (e.g. a stale/removed Code), same fallback hullerDisplayText uses.
    varietyDisplayText(code) {
      if (!code) return "";
      const variety = this.varietyOptions.find((v) => v.Code === code);
      return variety ? `${variety.Code} - ${variety.Description}` : code;
    },
    // §6: row click loads the detail panel in Review mode, populating the
    // existing Huller/Cracker value and Variety/Estimate rows (BR-03).
    onRowClicked(e) {
      if (!e.data) return;
      this.openSeason(e.data, { editMode: false, creating: false });
    },
    openSeason(row, { editMode, creating, pickingSeason }) {
      this.selectedSeason = row;
      this.editMode = editMode;
      this.creating = creating;
      this.pickingSeason = !!pickingSeason;
      this.workingHuller = "";
      this.workingRows = [];
      this.auditInfo = {};
      this.pendingEstimateChanges = [];

      if (!creating) {
        this.loadEstimateDetail(
          row.GrowerCode || this.model.SupplierCode,
          row.SeasonYear,
        );
      }
    },
    // §6/§9 (Story 2): "+ ADD NEW" opens the detail panel directly in Edit
    // mode (no Review step) against a Season picker whose candidate set is
    // all AlmondcoIntakeSeason values minus any Season already present in
    // this Grower's List data (client-side set subtraction).
    async addNewGrowerIntakeEstimate() {
      this.showLoading(true);
      try {
        const [seasonRes, usedRes] = await Promise.all([
          AlmondcoGrowerIntakeEstimateService.getSeasonOptions(),
          AlmondcoGrowerIntakeEstimateService.getUsedSeasons(
            this.model.SupplierCode,
          ),
        ]);
        const allSeasons = (seasonRes.data && seasonRes.data.value) || [];
        const usedSeasons = new Set(
          ((usedRes.data && usedRes.data.value) || []).map((r) => r.SeasonYear),
        );
        const available = allSeasons.filter((s) => !usedSeasons.has(s.Season));

        if (available.length === 0) {
          this.$notify(
            "Every available Season already has an estimate recorded for this Grower.",
            "error",
          );
          return;
        }

        this.seasonPickerOptions = available;
        this.openSeason(
          { SeasonYear: null, GrowerCode: this.model.SupplierCode },
          { editMode: true, creating: true, pickingSeason: true },
        );
      } finally {
        this.showLoading(false);
      }
    },
    // Once picked, SeasonYear locks in as a read-only identity field; the
    // Huller/Cracker field and Variety/Estimate grid start empty, using the
    // same validation/Save rules as an existing record's Edit mode.
    onSeasonPicked(e) {
      if (!e.value) return;
      this.selectedSeason = { ...this.selectedSeason, SeasonYear: e.value };
      this.pickingSeason = false;
      // #77392 fix: this is the moment the estimate grid actually mounts for
      // Add New (v-if="!pickingSeason" on the grid's own container) - the
      // grid ref does not exist yet until this flips, so the first blank row
      // is added here rather than back in openSeason/addNewGrowerIntakeEstimate.
      // $nextTick so the ref exists by the time this runs.
      this.$nextTick(() => this.ensureTrailingBlankVarietyRow());
    },
    async loadEstimateDetail(growerCode, seasonYear) {
      this.showLoading(true);
      try {
        const res = await AlmondcoGrowerIntakeEstimateService.getEstimateDetail(
          growerCode,
          seasonYear,
        );
        const rows = (res.data && res.data.value) || [];
        this.applyLoadedRows(rows);
      } catch (err) {
        this.workingRows = [];
      } finally {
        this.showLoading(false);
      }
    },
    applyLoadedRows(rows) {
      if (rows.length > 0) {
        this.workingHuller = rows[0].HullerCode;
        this.auditInfo = rows[0];
      }
      this.workingRows = rows.map((r) => ({
        RowKey: this.nextRowKey++,
        VarietyCode: r.VarietyCode,
        IntakeEstimate: r.IntakeEstimate,
      }));
    },
    enterEditMode() {
      if (!this.selectedSeason) return;
      this.editMode = true;
      // #77392 fix: an existing record's rows are all real (VarietyCode
      // filled) at this point - Review mode never showed a blank row, so
      // Edit mode needs its own trailing blank row added the moment editing
      // starts, same as scheme-prices.vue's own Review->Edit transition
      // handling. $nextTick since allow-adding is bound to editMode
      // reactively - the grid's own internal editing state needs a tick to
      // pick up the flip before addRow() is safe to call.
      this.$nextTick(() => this.ensureTrailingBlankVarietyRow());
    },
    closePanel() {
      this.selectedSeason = null;
      this.editMode = false;
      this.creating = false;
      this.pickingSeason = false;
      this.seasonPickerOptions = [];
      this.workingHuller = "";
      this.workingRows = [];
      this.pendingEstimateChanges = [];
      let grid = this.$refs["estimateGrid"].instance;
      grid.clearSelection();
    },
    onInitNewRow(e) {
      e.data.RowKey = this.nextRowKey++;
      e.data.VarietyCode = null;
      e.data.IntakeEstimate = 0;
    },
    // Grid's own native toolbar (Add row/Save changes/Discard changes icons,
    // search panel) is always hidden - replaced by the "+ ADD VARIETY" link
    // in the section label above, matching the prototype's own single
    // action row. Save changes/Discard changes aren't missed: the panel's
    // own floppy Save button calls grid.saveEditData() directly (see
    // saveEstimate()), independent of whether that toolbar button exists.
    onEstimateGridToolbarPreparing(e) {
      e.toolbarOptions.visible = false;
    },
    // Fires on every batch-mode pending add/edit/delete (editing.changes
    // updates live, well before Save) - snapshots it into
    // pendingEstimateChanges so mergedEstimateRows (and therefore
    // totalEstimate/rowCount) reflect the change immediately.
    //
    // #77392 fix: also the hook for the CX no-manual-add-row convention
    // (transaction-detail-grid convention, technical-design.md §6/AC -
    // already shipped elsewhere, e.g. Customer Receipt's Unallocated
    // Receipt grid) - every edit (a Variety picked, a row deleted, ...)
    // re-checks whether a trailing blank row still exists.
    onEstimateGridOptionChanged(e) {
      if (e.fullName && e.fullName.indexOf("editing.changes") === 0) {
        this.pendingEstimateChanges = e.value || [];
        this.ensureTrailingBlankVarietyRow();
      }
    },
    // Developer request (06Sep2026, no numbered ADO task): once a Variety is
    // picked, focus should move straight to that row's Intake Estimate cell
    // instead of DevExtreme's own default behaviour (closing the editor and
    // leaving focus wherever it lands - typically the same cell, not the
    // next line). Only the VarietyCode column needs this - the lookup's own
    // onSelectionChanged (added here, in onEditorPreparing, the same place
    // scheme-prices.vue's ItemCode branch hooks its own onSelectionChanged)
    // is the one event that fires exactly once a real selection is made, not
    // on every keystroke of the search box.
    //
    // Bug fix (07Sep2026, no numbered ADO task): onSelectionChanged also
    // fires DevExtreme's own initial resolve when the editor opens on a row
    // that already has a VarietyCode - not just on a genuine new pick.
    // dxSelectBox's SelectionChangedInfo carries only `selectedItem` (no
    // `event`, no `previousItem` - confirmed against the installed
    // DevExtreme type definitions), so that initial resolve is
    // indistinguishable from a real user selection by the event args alone.
    // Without a guard, clicking an already-populated Variety cell to edit it
    // fired this handler immediately with the row's own existing value,
    // which called focusIntakeEstimateCell and moved edit focus straight to
    // Intake Estimate before the user could interact with the dropdown at
    // all - the cell then closed/reverted, reading as "clicking it just
    // goes to an uneditable state." Capturing the row's VarietyCode at the
    // moment the editor is prepared (previousCode) and only moving focus
    // when the newly selected item's Code actually differs from it fixes
    // this - the initial resolve (same code) is now a no-op, while a
    // genuine change (new row, or an actual re-pick) still moves focus as
    // intended.
    onEstimateEditorPreparing(e) {
      if (e.parentType !== "dataRow") return;
      if (e.dataField !== "VarietyCode") return;

      const previousCode = e.row.data.VarietyCode;

      e.editorOptions.onSelectionChanged = (args) => {
        const newCode = args.selectedItem ? args.selectedItem.Code : null;
        if (newCode && newCode !== previousCode) {
          this.focusIntakeEstimateCell(e.row.rowIndex);
        }
      };
    },
    // getCellElement + focus + a deferred editCell - the same reliable
    // "move edit-focus to a specific cell" sequence already established
    // elsewhere in this codebase (e.g. journal-v2.vue/distribution-manifest.
    // vue's own focusOnEditableCell) rather than a bare editCell() call,
    // which past experience with this exact editCell API (see scheme-
    // prices.vue's own moveFocusToCell comment) has shown can land
    // unreliably without it.
    focusIntakeEstimateCell(rowIndex) {
      const grid =
        this.$refs.estimateDetailGrid && this.$refs.estimateDetailGrid.instance;
      if (!grid) return;

      const cellElement = grid.getCellElement(rowIndex, "IntakeEstimate");
      if (!cellElement) return;

      grid.focus(cellElement);
      setTimeout(() => {
        grid.editCell(rowIndex, "IntakeEstimate");
      }, 10);
    },
    // #77392 fix: batch-mode equivalent of scheme-prices.vue's own
    // ensureTrailingBlankRow - keeps exactly one blank row (VarietyCode
    // null, see onInitNewRow) at the end of the grid at all times while
    // editing, so picking a row's Variety auto-appends the next blank row
    // instead of requiring the manual "+ ADD VARIETY" click every time (that
    // button/addVarietyRow stays as a convenience alongside this, per the
    // Bug's own Expected Outcome - not removed).
    //
    // Keyed off mergedEstimateRows, not workingRows directly - batch mode
    // doesn't mutate workingRows until Save (see totalEstimate's own
    // comment), so mergedEstimateRows (the already-existing reconciled view
    // of workingRows + pending editing.changes) is the one place that
    // reflects what the grid is actually showing right now, blank row
    // included.
    //
    // Idempotent by construction, not by a separate flag: does nothing once
    // a blank row already exists, so this never loops or fires addRow()
    // repeatedly while a user is still typing IntakeEstimate on an
    // already-picked row (VarietyCode is the trigger field, matching
    // scheme-prices.vue's own PriceType-keyed trigger - IntakeEstimate
    // defaults to 0 and has no "required" validation rule, so there is no
    // equivalent "unset" state to wait on for that field).
    ensureTrailingBlankVarietyRow() {
      if (!this.editMode) return;
      const hasBlankRow = this.mergedEstimateRows.some((r) => !r.VarietyCode);
      if (hasBlankRow) return;
      const grid =
        this.$refs.estimateDetailGrid && this.$refs.estimateDetailGrid.instance;
      if (grid) grid.addRow();
    },
    addVarietyRow() {
      if (!this.editMode) return;
      this.$refs.estimateDetailGrid.instance.addRow();
    },
    onToolbarPreparing(e) {
      var toolbarItems = e.toolbarOptions.items;
      var searchPanel = toolbarItems.find((item) => {
        return item.name === "searchPanel";
      });

      e.toolbarOptions.items = toolbarItems.filter((item) => {
        return item.name === "searchPanel";
      });

      if (searchPanel) {
        searchPanel.location = "after";
      }

      if (this.canAddNew) {
        e.toolbarOptions.items.unshift({
          location: "before",
          widget: "dxButton",
          options: {
            icon: "plus",
            text: "Add New",
            onClick: () => {
              this.addNewGrowerIntakeEstimate();
            },
          },
        });
      }
    },
    // §5/§7/§9: full delete/update/insert reconciliation (BR-05). Empty-grid
    // Save is split by record state (BR-08/FLAG-IE-04): an existing record's
    // empty submission is confirmed then proceeds to the delete-all; a
    // brand-new Season is blocked outright instead - there is nothing yet in
    // the database to confirm deleting.
    async saveEstimate() {
      const grid = this.$refs["estimateDetailGrid"].instance;
      // #77392 fix: the always-present trailing blank row (VarietyCode null,
      // see ensureTrailingBlankVarietyRow) must be discarded from the grid's
      // own pending edit state BEFORE saveEditData() runs - VarietyCode's
      // own "required" validationRule (estimateColumns) would otherwise fail
      // native validation on that still-unfilled row every time Save is
      // clicked, and the hasEditData() check below would then silently
      // abort Save. See investigation.md's own Recommended fix #3 (Bug
      // #77392) for the full reasoning.
      this.discardBlankVarietyRows(grid);
      await grid.saveEditData();
      // Now just a defensive fallback (e.g. a genuine dataType/commit
      // error) - no business-rule validationRule remains on any column to
      // reject here since the bulk-validation change below; the real gate
      // is validateBeforeSave, checked next against the now-committed
      // workingRows.
      if (grid.hasEditData()) return;

      const blockingMessage = this.validateBeforeSave();
      if (blockingMessage) {
        await this.$alert(blockingMessage, "Unable to Save");
        return;
      }

      const workingRows = this.workingRows.filter(
        (variety) => variety.VarietyCode,
      );

      if (workingRows.length === 0) {
        if (this.creating) {
          await this.$alert(
            "At least one Variety/Estimate line is required.",
            "Unable to Save",
          );
          return;
        }

        const confirmed = await this.$confirm(
          `All estimates for Season ${this.selectedSeason.SeasonYear} will be deleted for this Grower. Continue?`,
          "Delete all estimates?",
        );
        if (!confirmed) return;
      }

      this.showLoading(true);
      try {
        const payload = {
          SeasonYear: this.selectedSeason.SeasonYear,
          GrowerCode: this.model.SupplierCode,
          HullerCode: this.workingHuller,
          IsNewRecord: this.creating,
          EstimateLines: workingRows.map((r) => ({
            VarietyCode: r.VarietyCode,
            IntakeEstimate: r.IntakeEstimate,
          })),
        };

        const res = await AlmondcoGrowerIntakeEstimateService.saveEstimate(
          payload,
        );
        const savedRows = (res && res.data) || [];

        this.editMode = false;
        this.creating = false;

        if (savedRows.length === 0) {
          this.$notify(
            `All Grower Intake Estimates for Season ${this.selectedSeason.SeasonYear} deleted.`,
            "success",
          );
          this.closePanel();
        } else {
          this.applyLoadedRows(savedRows);
          this.$notify("Grower Intake Estimate saved successfully", "success");
        }

        this.$refs["estimateGrid"].instance.refresh();
      } catch (err) {
        this.displayServerError(err, "Grower Intake Estimate save failed");
      } finally {
        this.showLoading(false);
      }
    },
    // Bulk-validation convention (developer direction, no numbered ADO task,
    // same convention as scheme-prices.vue's own validateBeforeSave): the
    // sole Save-time validation gate now, checked once against the full,
    // just-committed row set - replaces the DevExtreme inline
    // validationRules previously on hullerFormItems' Huller/Cracker field
    // (required) and estimateColumns' VarietyCode column (required + the
    // custom validateNoDuplicateVariety callback, both removed). Filters out
    // the always-present, still-unfilled trailing blank row (VarietyCode
    // null) first, the same way scheme-prices.vue filters its own
    // PriceType-null sentinel row - it is never a row the user has actually
    // entered anything into, whether that's the untouched trailing sentinel
    // or a row where only IntakeEstimate was typed without ever picking a
    // Variety (VarietyCode is this grid's own "has this row been started"
    // signal, same as PriceType is there - there is no separate flag).
    // Called from saveEstimate after discardBlankVarietyRows/saveEditData
    // have already run, so this.workingRows is the authoritative, current
    // state - not mergedEstimateRows, which existed only to reconcile
    // still-uncommitted batch edits before that commit happens.
    validateBeforeSave() {
      if (!this.workingHuller) {
        return "Huller is required.";
      }

      const rows = this.workingRows.filter((r) => r.VarietyCode);

      // Developer request (no numbered ADO task): report the specific
      // duplicated Variety by name, one at a time - not a generic "This
      // Variety..." message, and not every duplicate found in one pass.
      // Returning on the first match already gives the one-at-a-time
      // behaviour for free: fixing that row and clicking Save again re-runs
      // this same check from scratch and surfaces the next duplicate, if
      // any - no separate "which duplicate have I already reported" state
      // needed.
      const seen = new Set();
      for (const row of rows) {
        const code = (row.VarietyCode || "").toString().trim().toUpperCase();
        if (seen.has(code)) {
          return `Variety "${this.varietyDisplayText(
            row.VarietyCode,
          )}" has already been entered on another row. Remove or change the duplicate before saving.`;
        }
        seen.add(code);
      }

      return null;
    },
    // #77392 fix: discards any pending row still sitting at VarietyCode null
    // (the always-present trailing blank row - ensureTrailingBlankVarietyRow)
    // from the grid's own batch-mode edit state, before saveEditData() ever
    // runs - see saveEstimate's own comment for why this has to happen here,
    // not as a post-hoc payload filter the way scheme-prices.vue's cell-mode
    // grid handles its own equivalent PriceType-null sentinel row.
    //
    // Safe to call unconditionally: a still-blank row is always a
    // not-yet-saved "insert" pending change (RowKey assigned by onInitNewRow,
    // never a genuine committed row) - deleteRow on an uncommitted insert
    // simply discards it, it does not queue a delete of a row that was never
    // actually saved. Loops (with a safety cap) rather than assuming exactly
    // one blank row can ever exist, since this runs right before Save - the
    // one moment this method can't rely on ensureTrailingBlankVarietyRow's
    // own idempotency guard alone to prove there is only ever one.
    discardBlankVarietyRows(grid) {
      let rows = grid.getVisibleRows();
      let safety = rows.length + 1;
      while (safety-- > 0) {
        const idx = rows.findIndex(
          (r) => r.rowType === "data" && !r.data.VarietyCode,
        );
        if (idx === -1) break;
        grid.deleteRow(idx);
        rows = grid.getVisibleRows();
      }
    },
    // #77393 fix: the row's delete (trash) icon, wired as a custom button on
    // estimateColumns' buttons column instead of the `buttons: ["delete"]`
    // shorthand. DxEditing's own confirmDelete option is silently ignored by
    // DevExtreme whenever mode="batch" (confirmed against the installed
    // DevExtreme source - see investigation.md for Bug #77393), so this
    // method does the confirm step itself before ever calling deleteRow -
    // matching the same await this.$confirm(...)-then-deleteRow convention
    // used by every other confirm-before-delete grid in this codebase (e.g.
    // gc-popup-shifts.vue). Only once confirmed does deleteRow queue the row
    // as a pending "remove" change - the same DevExtreme batch-mode
    // mechanism (greyed-out row, reversible until Save) that was already
    // working correctly before this fix; the missing piece was purely the
    // confirmation gate ahead of it.
    async onDeleteVarietyRowClick(e) {
      const confirmed = await this.$confirm(
        "Remove this Variety/Estimate line? This takes effect once you Save.",
        "Delete",
      );
      if (confirmed) {
        e.component.deleteRow(e.row.rowIndex);

        const grid = this.$refs["estimateDetailGrid"].instance;
        await grid.saveEditData();
      }
    },
    backButtonFunction() {
      if (this.baseView.query && this.baseView.query.from) {
        this.$router.push({
          path: this.baseView.query.from,
        });
      } else
        this.$router.push({
          path: "/supplier",
        });
    },
  },
};
</script>

<style lang="scss">
.estimate-grid-card {
  margin-top: 12px;

  .estimate-grid-section-label {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 12px 0;
    font-size: 13px;
    font-weight: 600;
    color: #101828;

    .add-variety-link {
      display: flex;
      align-items: center;
      gap: 4px;
      background: none;
      border: none;
      padding: 0;
      font-size: 11px;
      font-weight: 600;
      color: #03a9f4;
      cursor: pointer;

      .dx-icon-plus {
        font-size: 13px;
      }

      &:disabled {
        color: #98a2b3;
        cursor: not-allowed;
      }
    }
  }

  .panel-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    // Right padding (5px) matches the grid's own .dx-data-row cell padding,
    // so the Total value's right edge lines up with the Intake Estimate
    // column's values above it.
    padding: 8px 5px 8px 12px;
    font-size: 12px;
    color: #667085;
    border-top: 1px solid rgba(0, 0, 0, 0.12);
  }

  .total-value {
    font-weight: 600;
    margin-left: 4px;
    color: #101828;
  }
}
</style>
